import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return instance;
  }, [token]);

  useEffect(() => {
    const fetchMember = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api().get('/auth/me');
        setMember(response.data);
      } catch (error) {
        console.error('Failed to fetch member:', error);
        localStorage.removeItem('auth_token');
        setToken(null);
        setMember(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [token, api]);

  const requestMagicLink = async (email) => {
    const response = await axios.post(`${API}/auth/magic-link`, { email });
    return response.data;
  };

  const verifyMagicLink = async (verifyToken) => {
    const response = await axios.post(`${API}/auth/verify`, { token: verifyToken });
    const { access_token, member: memberData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    setToken(access_token);
    setMember(memberData);
    
    return memberData;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setMember(null);
  };

  const updateMember = (updatedMember) => {
    setMember(updatedMember);
  };

  const value = {
    member,
    token,
    loading,
    isAuthenticated: !!member,
    isChair: member?.role === 'chair',
    api,
    requestMagicLink,
    verifyMagicLink,
    logout,
    updateMember,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
